import classNames from 'classnames';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header/Header';
import Tabs from '@/components/Tabs/Tabs';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useSTKContext } from '@/context/context';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useGetAllStores } from '@/services/stores/stores.service';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import _ from 'lodash/';
import { StoreAndGroupType } from '@/context/context.types';
import StoreGroupsTabItem from '@/pages/Franchisee/Menus/StoreGroupsTabItem/StoreGroupsTabItem';
import StoresTabItem from '@/pages/Franchisee/Menus/StoresTabItem/StoresTabItem';
import BundleAddOnsHeader from './BundleAddOnsHeader/BundleAddOnsHeader';
import styles from './FZBundleAddOns.module.css';

const FZBundleAddOns: FC = () => {
  const { t } = useTranslation();
  const { setStoresAndGroups, storesAndGroups } = useSTKContext();
  const { alias } = useFranchiseeAlias();
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const [selectedRows, setSelectedRows] = useState<{ [number: number]: boolean }>({});
  const { data: stores = [], isLoading, isError } = useGetAllStores(true);
  const [activeTab, setActiveTab] = useState(0);

  const selection = useMemo(() => {
    const storeList = storesAndGroups?.stores?.map((s) => s.storeNo) ?? [];
    const groupList = storesAndGroups?.storeGroups?.map((g) => g.storeGroup) ?? [];
    return { stores: storeList, groups: groupList };
  }, [storesAndGroups]);

  const storesGroup = useMemo(() => _.groupBy(stores, 'groupName'), [stores]);
  const storesSortedByStoreNo = useMemo(
    () => [...stores].sort((a, b) => Number(a.storeNo) - Number(b.storeNo)),
    [stores],
  );

  const handleTabClick = (indexOfClickedTab: number) => {
    setActiveTab(indexOfClickedTab);
  };

  if (isError) {
    showToast({
      toastType: ToastTypes.Error,
      label: t('error.fetchingStoreData'),
      id: 'storeFetchError',
    });
  }

  const saveStores = (storeNos: string[]) => {
    setStoresAndGroups({
      origin: 'tab-store',
      stores: storeNos.map((store) => ({
        storeGroup: stores.find((s) => s.storeNo === store)?.groupName,
        storeNo: store,
      })),
      countByStoreGroup: Object.entries(storesGroup).map(([groupName, storesData]) => ({
        storeGroup: groupName,
        count: storesData.length,
      })),
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return (
          <StoresTabItem
            isLoading={isLoading}
            stores={storesSortedByStoreNo}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onStoresSelectionChange={saveStores}
            storeLinkTo="/bundles/add-ons/view"
          />
        );
      default:
        return (
          <StoreGroupsTabItem
            stores={stores}
            selectedStores={selection.stores}
            selectedGroups={selection.groups as string[]}
            onStoresSelectionChange={(storeAndGroup: StoreAndGroupType) => setStoresAndGroups(storeAndGroup)}
            storeLinkTo="/bundles/add-ons/view"
          />
        );
    }
  };

  return (
    <div>
      <div className={styles.mainDiv}>
        <Header
          title={t('franchiseeBundleAddOns.title')}
          breadcrumb={{ to: '/', label: t('franchiseeBundleAddOns.breadcrumb') }}
        />
      </div>
      <div className={classNames(styles.page, { [styles.withAlias]: !alias, [styles.withFreeze]: freezeExists })}>
        <BundleAddOnsHeader totalStoreCount={stores.length} />
        <div className={styles.tableContainer}>
          <Tabs
            activeTab={activeTab}
            onTabClick={handleTabClick}
            tabs={[
              { label: t('franchiseeBundleAddOns.tabs.storeGroups'), id: 'groups' },
              { label: t('franchiseeBundleAddOns.tabs.allStores'), id: 'stores' },
            ]}
          />
          <div className={styles.tabContainer}>{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default FZBundleAddOns;
